/**
 * مكوّنات عرض نتيجة التقييم.
 *
 * قاعدة ملزمة: لا حساب هنا. كل قيمة تأتي جاهزة من ValuationResult.
 * الاستثناء الوحيد المسموح هو حساب **مواضع بكسل** للرسم (نسبة عرض شريط)،
 * وهو تخطيط بصري وليس منطق تقييم.
 */

import { color, verdictColor, verdictLabel } from '@/design/tokens';
import type { Comparable, ScoreComponent, VerdictLevel } from '@/domain/types';
import { num, perSqm, sar } from '@/lib/format';
import { PartialDataBadge } from '@/components/states/States';

// --------------------------------------------------------- Verdict Chip

export function VerdictChip({ verdict }: { verdict: VerdictLevel }): JSX.Element {
  const c = verdictColor[verdict];
  return (
    <span
      data-testid="verdict-chip"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 14px',
        borderRadius: 20,
        background: `${c}1A`,
        border: `1px solid ${c}55`,
        color: c,
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: c }} />
      {verdictLabel[verdict]}
    </span>
  );
}

// ------------------------------------------------- Fair Value Range Bar

export function FairValueRangeBar({
  low,
  high,
  asking,
}: {
  low: number;
  high: number;
  asking: number;
}): JSX.Element {
  // مدى العرض البصري: توسيع النطاق 35% من كل جهة حتى تبقى علامة السعر داخل الإطار.
  const pad = (high - low) * 0.35 || high * 0.05;
  const min = Math.min(low - pad, asking - pad * 0.5);
  const max = Math.max(high + pad, asking + pad * 0.5);
  const at = (v: number): number => ((v - min) / (max - min || 1)) * 100;

  return (
    <div data-testid="fair-value-bar">
      <div
        style={{
          position: 'relative',
          height: 56,
          borderRadius: 12,
          background: color.field,
          border: `1px solid ${color.hairlineSoft}`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            insetInlineStart: `${at(low)}%`,
            width: `${at(high) - at(low)}%`,
            background: 'rgba(0,229,199,.16)',
            borderInline: `1px solid ${color.tealBorder}`,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: 6,
            bottom: 6,
            insetInlineStart: `${at(asking)}%`,
            width: 2,
            background: color.amber,
            transform: 'translateX(50%)',
          }}
          aria-hidden="true"
        />
      </div>
      {/*
        التسميات معلّقة على مواضعها الحقيقية على المحور، لا على طرفَي الشريط.

        العلّة التي أوجبته: المحور موسَّع من الجهتين حتى تبقى علامة السعر داخل
        الإطار، فالحزمة لا تملأ الشريط. ووضع الحدّين عند الطرفين كان يجعل
        الرقمين يشيران إلى موضعين ليسا موضعهما — فيُقرأ الشريط كرسم زخرفي لا
        كمحور. ربطُ كل رقم بموضعه يجعل المسافة بين السعر والنطاق مقروءة بالعين.
      */}
      <div style={{ position: 'relative', height: 18, marginTop: 8 }}>
        <span
          className="nq-num"
          dir="ltr"
          style={{
            position: 'absolute',
            insetInlineStart: `${at(low)}%`,
            transform: 'translateX(50%)',
            fontSize: 11.5,
            color: color.label,
            whiteSpace: 'nowrap',
          }}
        >
          {num(low)}
        </span>
        <span
          className="nq-num"
          dir="ltr"
          style={{
            position: 'absolute',
            insetInlineStart: `${at(high)}%`,
            transform: 'translateX(50%)',
            fontSize: 11.5,
            color: color.label,
            whiteSpace: 'nowrap',
          }}
        >
          {num(high)}
        </span>
        <span
          className="nq-num"
          dir="ltr"
          style={{
            position: 'absolute',
            insetInlineStart: `${at(asking)}%`,
            transform: 'translateX(50%)',
            fontSize: 11.5,
            fontWeight: 600,
            color: color.amber,
            whiteSpace: 'nowrap',
          }}
        >
          {num(asking)}
        </span>
      </div>
      <div style={{ marginTop: 10, fontSize: 11.5, color: color.micro }}>
        الحزمة التركوازية نطاق القيمة العادلة، والعلامة الكهرمانية السعر المطلوب.
      </div>
    </div>
  );
}

// ------------------------------------------------------ Opportunity Ring

/**
 * مؤشر الفرصة — حلقة تركوازية صغيرة.
 * ممنوع أن يشترك في اللون أو الشكل مع «الثقة في التقييم».
 */
export function OpportunityRing({ score }: { score: number }): JSX.Element {
  const size = 74;
  const r = 32;
  const circumference = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference;

  return (
    <div
      data-testid="opportunity-ring"
      style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
    >
      <svg width={size} height={size} viewBox="0 0 74 74" role="img" aria-label={`مؤشر الفرصة ${score} من 100`}>
        <circle cx="37" cy="37" r={r} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="5" />
        <circle
          cx="37"
          cy="37"
          r={r}
          fill="none"
          stroke={color.teal}
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform="rotate(-90 37 37)"
        />
        <text
          x="37"
          y="42"
          textAnchor="middle"
          fill={color.text}
          fontSize="19"
          fontWeight="600"
          fontFamily="inherit"
        >
          {score}
        </text>
      </svg>
      <div style={{ minWidth: 0 }}>
        <div className="nq-label">مؤشر الفرصة</div>
        <div className="nq-num" dir="ltr" style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>
          {score} / 100
        </div>
        <p style={{ marginTop: 6, fontSize: 11.5, color: color.micro, lineHeight: 1.6 }}>
          ترتيب جاذبية — ليس نسبة دقة.
        </p>
      </div>
    </div>
  );
}

// ------------------------------------------------------- Confidence Card

/**
 * الثقة في التقييم — بطاقة منفصلة، شريط أفقي، رمادي أو كهرماني.
 * ممنوع منعاً باتاً استخدام التركوازي هنا.
 */
export function ConfidenceCard({
  score,
  explanation,
}: {
  score: number;
  explanation: string;
}): JSX.Element {
  const low = score < 60;
  const barColor = low ? color.amber : color.neutralNumber;

  return (
    <div className="nq-card nq-card--secondary" data-testid="confidence-card">
      <div className="nq-label">الثقة في التقييم</div>
      <div
        className="nq-num"
        dir="ltr"
        style={{ fontSize: 26, fontWeight: 600, marginTop: 6, color: barColor }}
      >
        {score}%
      </div>
      <div
        style={{
          marginTop: 12,
          height: 6,
          borderRadius: 4,
          background: 'rgba(255,255,255,.07)',
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${score}%`, height: '100%', background: barColor }} />
      </div>
      <p style={{ marginTop: 12, fontSize: 12.5, color: color.textSubtle, lineHeight: 1.7 }}>
        {explanation}
      </p>
    </div>
  );
}

// --------------------------------------------------- Score Component Card

export function ScoreComponentCard({ component }: { component: ScoreComponent }): JSX.Element {
  const ratio = component.max > 0 ? component.value / component.max : 0;
  return (
    <div className="nq-card nq-card--secondary" style={{ flex: '1 1 190px' }}>
      <div className="nq-label">{component.label}</div>
      <div className="nq-num" dir="ltr" style={{ fontSize: 22, fontWeight: 600, marginTop: 6 }}>
        {component.value} / {component.max}
      </div>
      <div
        style={{
          marginTop: 10,
          height: 4,
          borderRadius: 3,
          background: 'rgba(255,255,255,.07)',
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${ratio * 100}%`, height: '100%', background: color.teal }} />
      </div>
      <p style={{ marginTop: 10, fontSize: 11.5, color: color.micro, lineHeight: 1.6 }}>
        {component.note}
      </p>
    </div>
  );
}

// ---------------------------------------------------- Component Breakdown

export function ComponentBreakdown({ components }: { components: ScoreComponent[] }): JSX.Element {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {components.map((c) => (
        <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ flex: '0 0 96px', fontSize: 13, color: color.textBody }}>{c.label}</span>
          <span
            style={{
              flex: '1 1 140px',
              height: 8,
              borderRadius: 5,
              background: 'rgba(255,255,255,.07)',
              overflow: 'hidden',
              minWidth: 100,
            }}
          >
            <span
              style={{
                display: 'block',
                width: `${(c.value / c.max) * 100}%`,
                height: '100%',
                background: color.teal,
              }}
            />
          </span>
          <span className="nq-num" dir="ltr" style={{ flex: '0 0 62px', fontSize: 12.5, color: color.textMuted }}>
            {c.value} / {c.max}
          </span>
        </div>
      ))}
    </div>
  );
}

// ----------------------------------------------------- Comparables Table

export function ComparablesTable({
  comparables,
  partial,
  expected,
}: {
  comparables: Comparable[];
  partial: boolean;
  expected: number;
}): JSX.Element {
  return (
    <div>
      {partial ? (
        <div style={{ marginBottom: 12 }}>
          <PartialDataBadge used={comparables.length} expected={expected} />
        </div>
      ) : null}
      <div className="nq-table-scroll">
        <table className="nq-table">
          <thead>
            <tr>
              <th>التاريخ</th>
              <th>المساحة</th>
              <th>سعر المتر</th>
              <th>المسافة</th>
              <th>مقارنة بالمطلوب</th>
            </tr>
          </thead>
          <tbody>
            {comparables.map((c) => (
              <tr key={c.id}>
                <td className="nq-num" dir="ltr">
                  {c.date}
                </td>
                <td className="nq-num" dir="ltr">
                  {num(c.areaSqm)} م²
                </td>
                <td className="nq-num" dir="ltr">
                  {perSqm(c.pricePerSqm)}
                </td>
                <td className="nq-num" dir="ltr">
                  {c.distanceLabel}
                </td>
                <td
                  className="nq-num"
                  dir="ltr"
                  style={{ color: c.deltaPct < 0 ? color.red : color.green }}
                >
                  {c.deltaPct > 0 ? '+' : ''}
                  {c.deltaPct}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------------------------------------ Price summary row

export function PriceSummary({
  fairLow,
  fairHigh,
  asking,
  askingPerSqm,
  /** التسمية تأتي من الخادم — الواجهة لا تقرر ماذا تسمّي الرقم. */
  rangeLabel = 'نطاق القيمة العادلة',
  basis,
}: {
  fairLow: number;
  fairHigh: number;
  asking: number;
  askingPerSqm: number;
  rangeLabel?: string;
  basis?: string;
}): JSX.Element {
  return (
    <div className="nq-row" style={{ gap: 20 }}>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        {/*
          النطاق أكبر من السعر المطلوب عمداً.

          كان الرقمان بالحجم نفسه، ورقمُ المستخدم أعلى تبايناً ووحدته ملاصقة،
          بينما وحدة النطاق مفصولة في سطر باهت — فتذهب العين إلى ما كتبه
          المستخدم بنفسه لا إلى الجواب الذي جاء يطلبه. الجواب هو البطل.
        */}
        <div className="nq-label">{rangeLabel}</div>
        <div
          className="nq-num"
          dir="ltr"
          style={{
            fontSize: 30,
            fontWeight: 700,
            marginTop: 6,
            color: color.teal,
            lineHeight: 1.25,
          }}
        >
          {num(fairLow)}–{num(fairHigh)}{' '}
          <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.75 }}>ر.س</span>
        </div>
        {basis ? (
          <p style={{ marginTop: 8, fontSize: 11.5, color: color.micro, lineHeight: 1.6 }}>{basis}</p>
        ) : null}
      </div>
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div className="nq-label">السعر المطلوب</div>
        <div
          className="nq-num"
          dir="ltr"
          style={{ fontSize: 21, fontWeight: 600, marginTop: 6, color: color.textSubtle }}
        >
          {sar(asking)}
        </div>
        <div className="nq-num" dir="ltr" style={{ fontSize: 11.5, color: color.micro, marginTop: 2 }}>
          {perSqm(askingPerSqm)}
        </div>
      </div>
    </div>
  );
}
